import { BellRinging, Plus, TrashSimple } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { useOptionalFollowUp } from '@/app/providers/follow-up-provider';
import { createFollowUpRepository } from '@/data/repositories/follow-up-repository';
import type { FollowUpChannel, Uuid } from '@/data/db';
import { Badge } from '@/components/primitives/badge';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { IconButton } from '@/components/primitives/icon-button';
import { Field, Select, TextArea, TextInput } from '@/components/primitives/field';
import { useToast } from '@/components/feedback/toast';
import { nowLocalDateTime } from '@/lib/clock';
import { useActiveContacts } from '@/features/contacts/use-contacts';
import { SupervisorReminderEditor, REMINDER_CHANNEL_LABELS } from './supervisor-reminder-editor';
import { useFollowUpLogs, useSupervisorReminders } from './use-reminders';

export const FOLLOW_UP_CHANNEL_LABELS: Record<FollowUpChannel, string> = {
  in_person: '當面',
  phone: '電話',
  email: '電子郵件',
  message: '訊息',
  meeting: '會議',
  other: '其他',
};

const REMINDER_STATE_LABELS: Record<string, string> = {
  pending: '待提醒',
  snoozed: '已稍後',
  done: '已提醒',
  cancelled: '已取消',
};

/** 每次聯繫都形成獨立歷史（spec 11）。 */
export function FollowUpTimeline({ taskId }: { taskId: Uuid }) {
  const { db, save } = useWorkspace();
  const toast = useToast();
  const followUp = useOptionalFollowUp();
  const repository = useMemo(() => createFollowUpRepository(db), [db]);

  const logs = useFollowUpLogs(taskId);
  const reminders = useSupervisorReminders(taskId);
  const contacts = useActiveContacts();

  const [adding, setAdding] = useState(false);
  const [remindering, setRemindering] = useState(false);

  const contactName = (id: Uuid) =>
    contacts?.find((contact) => contact.id === id)?.name ?? '（已刪除）';

  async function markReminded(reminderId: Uuid) {
    try {
      await save.run(async () => {
        await repository.markReminded(reminderId, nowLocalDateTime());
      });
      // 自動建立追蹤紀錄後，接著問「這件事結案了嗎」。
      followUp?.askFollowUp(reminderId);
    } catch (error) {
      toast.show({
        message: '沒有存成功',
        detail: error instanceof Error ? error.message : undefined,
        tone: 'danger',
      });
    }
  }

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[0.8125rem] font-medium text-text-muted">主管提醒</h3>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<BellRinging size={14} />}
            onClick={() => setRemindering(true)}
          >
            設定提醒
          </Button>
        </div>

        {reminders === undefined ? (
          <p className="text-[0.8125rem] text-text-muted">載入中…</p>
        ) : reminders.length === 0 ? (
          <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-[0.8125rem] text-text-muted">
            還沒有設定主管提醒。
          </p>
        ) : (
          <ul className="space-y-1.5">
            {reminders.map((reminder) => (
              <li
                key={reminder.id}
                className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-3 py-2.5"
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-[0.875rem]">{reminder.message}</p>
                    <p className="flex flex-wrap items-center gap-2 text-[0.75rem] text-text-muted">
                      <span>{contactName(reminder.supervisorContactId)}</span>
                      <span className="tabular">
                        {(reminder.snoozedUntilLocal ?? reminder.remindAtLocal).replace('T', ' ')}
                      </span>
                      <span>{REMINDER_CHANNEL_LABELS[reminder.channel]}</span>
                      <Badge tone={reminder.state === 'done' ? 'success' : 'accent'}>
                        {REMINDER_STATE_LABELS[reminder.state]}
                      </Badge>
                    </p>
                  </div>

                  {(reminder.state === 'pending' || reminder.state === 'snoozed') && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void markReminded(reminder.id)}
                    >
                      已提醒
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[0.8125rem] font-medium text-text-muted">追蹤紀錄</h3>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Plus size={14} />}
            onClick={() => setAdding(true)}
          >
            記一筆
          </Button>
        </div>

        {logs === undefined ? (
          <p className="text-[0.8125rem] text-text-muted">載入中…</p>
        ) : logs.length === 0 ? (
          <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-[0.8125rem] text-text-muted">
            還沒有聯繫紀錄。打過電話、寄過信之後記一筆，之後就知道進度到哪。
          </p>
        ) : (
          <ol className="space-y-2 border-l border-border pl-4">
            {logs.map((log) => (
              <li key={log.id} className="relative">
                <span
                  aria-hidden="true"
                  className="absolute top-2 -left-[1.3125rem] size-2 rounded-full bg-accent"
                />
                <div className="group rounded-[var(--radius-control-lg)] bg-surface-sunken px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="tabular flex flex-wrap items-center gap-2 text-[0.75rem] text-text-muted">
                        <span>{log.occurredAtLocal.replace('T', ' ')}</span>
                        <span>{FOLLOW_UP_CHANNEL_LABELS[log.channel]}</span>
                        {log.contactIds.map((id) => (
                          <span key={id}>{contactName(id)}</span>
                        ))}
                        {log.supervisorReminderId && <Badge tone="accent">來自主管提醒</Badge>}
                      </p>
                      <p className="text-[0.875rem] leading-relaxed">{log.summary}</p>
                      {log.response && (
                        <p className="text-[0.8125rem] leading-relaxed text-text-muted">
                          對方回覆：{log.response}
                        </p>
                      )}
                      {log.nextFollowUpAtLocal && (
                        <p className="tabular text-[0.75rem] text-warning">
                          下次追蹤：{log.nextFollowUpAtLocal.replace('T', ' ')}
                        </p>
                      )}
                    </div>

                    <IconButton
                      label="刪除這筆紀錄"
                      tooltipSide="left"
                      icon={<TrashSimple size={14} />}
                      className="size-8 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
                      onClick={() =>
                        void save.run(async () => {
                          await repository.softDeleteLog(log.id);
                          toast.show({
                            message: '已移入垃圾桶',
                            action: {
                              label: '復原',
                              onAction: () => void repository.restoreLog(log.id),
                            },
                          });
                        })
                      }
                    />
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {adding && <AddLogDialog taskId={taskId} open onOpenChange={setAdding} />}
      {remindering && (
        <SupervisorReminderEditor taskId={taskId} open onOpenChange={setRemindering} />
      )}
    </div>
  );
}

function AddLogDialog({
  taskId,
  open,
  onOpenChange,
}: {
  taskId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { db, save } = useWorkspace();
  const toast = useToast();
  const repository = useMemo(() => createFollowUpRepository(db), [db]);

  const [occurredAtLocal, setOccurredAtLocal] = useState(() => nowLocalDateTime());
  const [channel, setChannel] = useState<FollowUpChannel>('phone');
  const [summary, setSummary] = useState('');
  const [response, setResponse] = useState('');
  const [nextFollowUpAtLocal, setNextFollowUpAtLocal] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!summary.trim()) {
      setError('寫一句這次做了什麼，之後才看得懂。');
      return;
    }

    try {
      await save.run(async () => {
        await repository.addLog({
          taskId,
          occurredAtLocal,
          channel,
          summary: summary.trim(),
          response: response.trim() || null,
          nextFollowUpAtLocal: nextFollowUpAtLocal || null,
        });
      });
      toast.show({ message: '已記下這次聯繫', tone: 'success' });
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '沒有存成功，可以再試一次。');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="記一筆追蹤紀錄"
      description="每次聯繫都留下獨立的紀錄，之後才追得回進度。"
      size="md"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button variant="primary" size="sm" onClick={() => void submit()}>
            記下來
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="聯繫時間">
            {({ id }) => (
              <TextInput
                id={id}
                type="datetime-local"
                value={occurredAtLocal}
                onChange={(event) => setOccurredAtLocal(event.target.value)}
              />
            )}
          </Field>
          <Field label="用什麼方式">
            {({ id }) => (
              <Select
                id={id}
                value={channel}
                onValueChange={(value) => setChannel(value as FollowUpChannel)}
                options={Object.entries(FOLLOW_UP_CHANNEL_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </Field>
        </div>

        <Field label="這次處理了什麼" error={error}>
          {({ id, describedBy }) => (
            <TextArea
              id={id}
              aria-describedby={describedBy}
              value={summary}
              placeholder="例如：已致電廠商確認交期"
              onChange={(event) => {
                setSummary(event.target.value);
                if (error) setError(null);
              }}
            />
          )}
        </Field>

        <Field label="對方怎麼說" hint="還沒回覆就先留空。">
          {({ id }) => (
            <TextArea
              id={id}
              value={response}
              placeholder="例如：對方說週三給答覆"
              onChange={(event) => setResponse(event.target.value)}
            />
          )}
        </Field>

        <Field label="下一次追蹤時間" hint="填了之後，超過這個時間就會出現在今日桌面。">
          {({ id }) => (
            <TextInput
              id={id}
              type="datetime-local"
              value={nextFollowUpAtLocal}
              onChange={(event) => setNextFollowUpAtLocal(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
